import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Grid,
  Collapse,
  Button,
  Tooltip,
} from "@mui/material";
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from "@mui/icons-material";
import RichTextEditor from "./RichTextEditor";
import FileUpload, { AttachmentPreview } from "./FileUpload";

// Typography options (hardcoded as requested)
const TYPOGRAPHY_OPTIONS = [
  "Roboto Bold 24px",
  "Roboto Medium 20px",
  "Roboto Regular 18px",
  "Inter Bold 24px",
  "Inter Medium 20px",
  "Inter Regular 18px",
  "Open Sans Bold 22px",
  "Open Sans Medium 18px",
  "Open Sans Regular 16px",
  "Outfit Bold 24px",
  "Outfit Medium 20px",
  "Outfit Regular 18px",
];

const HEADING_LEVELS = ["H2", "H3", "H4", "H5", "H6"];

const SectionCard = ({
  section,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
  onAttachmentReorder,
  currentGuideId = null,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [draggedAttachmentIndex, setDraggedAttachmentIndex] = useState(null);
  const [dragOverAttachmentIndex, setDragOverAttachmentIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleFieldChange = (field, value) => {
    onUpdate({
      ...section,
      [field]: value,
    });
  };

  const handleAttachmentAdd = (attachments) => {
    const newAttachments = [
      ...(section.attachments || []),
      ...attachments.map((att) => ({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: att.type,
        url: att.url,
        filename: att.filename,
        size: att.size,
      })),
    ];
    handleFieldChange("attachments", newAttachments);
  };

  const handleAttachmentRemove = (attachmentId) => {
    const newAttachments = (section.attachments || []).filter(
      (att) => att.id !== attachmentId
    );
    handleFieldChange("attachments", newAttachments);
  };

  const handleAttachmentDragStart = (e, attachmentIndex) => {
    setDraggedAttachmentIndex(attachmentIndex);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", attachmentIndex.toString());
  };

  const handleAttachmentDragOver = (e, attachmentIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedAttachmentIndex !== null && draggedAttachmentIndex !== attachmentIndex) {
      setDragOverAttachmentIndex(attachmentIndex);
    }
  };

  const handleAttachmentDragEnter = (e, attachmentIndex) => {
    e.preventDefault();
    if (draggedAttachmentIndex !== null && draggedAttachmentIndex !== attachmentIndex) {
      setDragOverAttachmentIndex(attachmentIndex);
    }
  };

  const handleAttachmentDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're leaving the attachment itself
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverAttachmentIndex(null);
    }
  };

  const handleAttachmentDrop = (e, attachmentIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedAttachmentIndex === null || draggedAttachmentIndex === attachmentIndex) {
      setDraggedAttachmentIndex(null);
      setDragOverAttachmentIndex(null);
      return;
    }

    const attachments = [...(section.attachments || [])];
    const draggedItem = attachments[draggedAttachmentIndex];
    attachments.splice(draggedAttachmentIndex, 1);
    attachments.splice(attachmentIndex, 0, draggedItem);
    setDraggedAttachmentIndex(null);
    setDragOverAttachmentIndex(null);
    handleFieldChange("attachments", attachments);
  };

  const handleAttachmentDragEnd = () => {
    setDraggedAttachmentIndex(null);
    setDragOverAttachmentIndex(null);
  };

  const handleTextChange = (value) => {
    handleFieldChange("text_html", value);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragOver) {
      onDragOver(e, index);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    if (onDrop) {
      onDrop(e, index);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're leaving the card itself, not a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  return (
    <Card
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        if (onDragStart) {
          onDragStart(e, index);
        }
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        mb: 2,
        border: `2px solid ${
          isDragging
            ? "#9048FB"
            : dragOverIndex === index
            ? "#7c3aed"
            : "#e5e7eb"
        }`,
        borderRadius: "12px",
        transition: "all 0.2s ease",
        opacity: isDragging ? 0.5 : 1,
        transform: isDragging ? "scale(0.98)" : "scale(1)",
        backgroundColor:
          dragOverIndex === index && !isDragging ? "#f3e8ff" : "#ffffff",
        boxShadow:
          isDragging || dragOverIndex === index
            ? "0 4px 12px rgba(144, 72, 251, 0.2)"
            : "0 1px 3px rgba(0, 0, 0, 0.1)",
        "&:hover": {
          borderColor: "#9048FB",
          boxShadow: "0 2px 8px rgba(144, 72, 251, 0.1)",
        },
      }}
    >
      <CardContent>
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            pb: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DragIcon
              sx={{
                color: "#9ca3af",
                cursor: "move",
                "&:hover": { color: "#9048FB" },
              }}
            />
            <Typography
              sx={{
                fontFamily: "Outfit",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Section {index + 1}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Move Up (Keyboard: Arrow Up)">
              <IconButton
                size="small"
                onClick={() => onMoveUp && onMoveUp(index)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onMoveUp && onMoveUp(index);
                  }
                }}
                disabled={index === 0}
                aria-label={`Move section ${index + 1} up`}
                sx={{
                  color: "#6b7280",
                  "&:hover": { color: "#9048FB" },
                  "&:focus": {
                    outline: "2px solid #9048FB",
                    outlineOffset: "2px",
                  },
                }}
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Move Down (Keyboard: Arrow Down)">
              <IconButton
                size="small"
                onClick={() => onMoveDown && onMoveDown(index)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onMoveDown && onMoveDown(index);
                  }
                }}
                aria-label={`Move section ${index + 1} down`}
                sx={{
                  color: "#6b7280",
                  "&:hover": { color: "#9048FB" },
                  "&:focus": {
                    outline: "2px solid #9048FB",
                    outlineOffset: "2px",
                  },
                }}
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={expanded ? "Collapse" : "Expand"}>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  color: "#6b7280",
                  "&:hover": { color: "#9048FB" },
                }}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate Section">
              <IconButton
                size="small"
                onClick={() => onDuplicate && onDuplicate(index)}
                sx={{
                  color: "#10b981",
                  "&:hover": { color: "#059669", backgroundColor: "#d1fae5" },
                }}
              >
                <DuplicateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Section">
              <IconButton
                size="small"
                onClick={() => onDelete && onDelete(index)}
                sx={{
                  color: "#ef4444",
                  "&:hover": { color: "#dc2626", backgroundColor: "#fee2e2" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Heading Input */}
            <TextField
              label="Section Heading (Optional)"
              value={section.heading || ""}
              onChange={(e) => handleFieldChange("heading", e.target.value)}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Outfit",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Outfit",
                },
              }}
            />

            {/* Heading Level and Typography Selectors */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontFamily: "Outfit" }}>
                    Heading Level
                  </InputLabel>
                  <Select
                    value={section.heading_level || "H2"}
                    onChange={(e) =>
                      handleFieldChange("heading_level", e.target.value)
                    }
                    label="Heading Level"
                    disabled={!section.heading || section.heading.trim() === ""}
                    sx={{ fontFamily: "Outfit" }}
                  >
                    {HEADING_LEVELS.map((level) => (
                      <MenuItem key={level} value={level} sx={{ fontFamily: "Outfit" }}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontFamily: "Outfit" }}>
                    Typography
                  </InputLabel>
                  <Select
                    value={section.typography || TYPOGRAPHY_OPTIONS[0]}
                    onChange={(e) =>
                      handleFieldChange("typography", e.target.value)
                    }
                    label="Typography"
                    sx={{ fontFamily: "Outfit" }}
                  >
                    {TYPOGRAPHY_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option} sx={{ fontFamily: "Outfit" }}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Rich Text Editor */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 1,
                }}
              >
                Content
              </Typography>
              <RichTextEditor
                value={section.text_html || ""}
                onChange={handleTextChange}
                placeholder="Enter section content..."
                currentGuideId={currentGuideId}
              />
            </Box>

            {/* Attachments Section */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 500,
                  color: "#374151",
                  mb: 1,
                }}
              >
                Attachments (Images/Videos)
              </Typography>
              <FileUpload
                onUploadSuccess={handleAttachmentAdd}
                onUploadError={(error) => console.error("Upload error:", error)}
                maxFiles={10}
              />

              {/* Attachment Previews */}
              {section.attachments && section.attachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {section.attachments.map((attachment, attIndex) => (
                      <Grid item xs={6} sm={4} md={3} key={attachment.id}>
                        <AttachmentPreview
                          attachment={attachment}
                          onRemove={handleAttachmentRemove}
                          onDragStart={handleAttachmentDragStart}
                          onDragOver={handleAttachmentDragOver}
                          onDragEnter={handleAttachmentDragEnter}
                          onDragLeave={handleAttachmentDragLeave}
                          onDrop={handleAttachmentDrop}
                          onDragEnd={handleAttachmentDragEnd}
                          isDragging={draggedAttachmentIndex === attIndex}
                          isDragOver={dragOverAttachmentIndex === attIndex}
                          index={attIndex}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default SectionCard;

